import { useEffect, useCallback, useState } from 'react';
import {
  useBlocker,
  useBeforeUnload,
  useNavigate,
  BlockerFunction,
  Location,
} from 'react-router-dom';
import Popup, { ActionButton } from './Popup/Popup';
import { AgxBodyText, Images } from '@urbanx/agx-ui-components';

interface Props {
  hasUnsavedChanges: boolean;
  forceCheck?: boolean;
  resetForceCheck?: (value: boolean) => void;
  onBack?: () => void;
}

export const FormPrompt = (props: Props) => {
  const { hasUnsavedChanges, forceCheck, resetForceCheck, onBack } = props;
  const navigate = useNavigate();
  const [showPopUp, setShowPopUp] = useState(false);
  const [discardChanges, setDiscardChanges] = useState(false);
  const [nextLocation, setNextLocation] = useState<Location | null>(null);
  const onLocationChange = useCallback<BlockerFunction>(
    ({ nextLocation }) => {
      if (!hasUnsavedChanges || discardChanges || nextLocation.hash !== '') {
        return false;
      }

      setNextLocation(nextLocation);
      setShowPopUp(true);
      return true;
    },
    [discardChanges, hasUnsavedChanges]
  );
  const blocker = useBlocker(hasUnsavedChanges && !discardChanges ? onLocationChange : false);

  const resetPromptState = useCallback(() => {
    setShowPopUp(false);
    setDiscardChanges(false);
    setNextLocation(null);
  }, []);

  const cancelNavigation = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
    resetPromptState();
    if (resetForceCheck) resetForceCheck(false);
  }, [blocker, resetForceCheck, resetPromptState]);

  const actionButtons: ActionButton[] = [
    {
      id: 'go-back',
      title: 'Go Back',
      clickHandler: cancelNavigation,
    },
    {
      id: 'discard-changes',
      title: 'Discard changes',
      isPrimary: true,
      clickHandler: () => {
        setShowPopUp(false);

        if (blocker.state === 'blocked') {
          blocker.proceed();
          return;
        }

        setDiscardChanges(true);
      },
    },
  ];

  useBeforeUnload(
    useCallback(
      (event) => {
        if (hasUnsavedChanges && !showPopUp) {
          event.preventDefault();
          /**
           * Since Firefox 44, Chrome 51, Opera 38, and Safari 9.1,
           * a generic string not under the control of the webpage is shown instead of the returned string.
           *
           * For any older browser versions return the following prompt string
           */
          event.returnValue = 'Do you still wish to leave the page?';
        }
      },
      [hasUnsavedChanges, showPopUp]
    ),
    { capture: true }
  );

  useEffect(() => {
    if (forceCheck) setShowPopUp(true);
  }, [forceCheck]);

  useEffect(() => {
    if (!discardChanges) {
      return;
    }

    if (forceCheck && onBack) {
      onBack();
      if (resetForceCheck) resetForceCheck(false);
      resetPromptState();
      return;
    }

    if (nextLocation) {
      navigate(nextLocation);
      resetPromptState();
    }
  }, [
    discardChanges,
    forceCheck,
    navigate,
    nextLocation,
    onBack,
    resetForceCheck,
    resetPromptState,
  ]);

  return (
    <Popup
      title={`You have unsaved changes`}
      isOpen={showPopUp}
      onClose={cancelNavigation}
      actionButtons={actionButtons}
      size={{ widthInPX: 640 }}
      icon={<Images.AlertCircleOutline />}
    >
      <div>
        <AgxBodyText medium>Do you still wish to leave the page?</AgxBodyText>
      </div>
    </Popup>
  );
};
